// Every case action in one place: Move / Hold / Resume / Cancel / Reopen / Assign / Note / Approve /
// Review / Production / Reprint-Rework / Dispatch / Upload / Setup version. Used by the board, the
// table and the case workspace so behaviour is identical everywhere.
import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { WF_STAGE_ROLES as R, WF_APPROVAL_KINDS, WF_APPROVAL_KIND_LABELS, WF_FILE_CATEGORIES, WF_NOTE_KINDS, WF_SETUP_KINDS, alignerQuote, type AlignerDiscount, isDerivedDueDate, turnaroundDueDate, type WfApprovalKind, type WfCase, type WfFile, type WfStage } from '@alignova/shared';
import { TurnaroundPicker } from '../../ui/TurnaroundPicker.tsx';

/** Files worth attaching to a setup version — what a doctor can actually review. */
const SETUP_MEDIA = new Set(['video', 'setup', 'treatment_plan', 'screenshot', 'image', 'pdf']);
import { useAuth } from '../../auth/AuthContext.tsx';
import { isDeferred } from '../../lib/api.ts';
import { useAlignerPricing } from '../../lib/useAlignerPricing.ts';
import type { UploadStats } from '../../lib/uploadWithProgress.ts';
import { isArchiveOutcome, uploadCaseFile, useWfConfig, wfApi } from './workflowApi.ts';
import { Btn, ErrorText, Field, Modal, inputCls } from './wfUi.tsx';
import { DiscountInput, QuoteLine } from './DiscountInput.tsx';
import { ArchTrayInputs, PRODUCTION_COUNT_STAGES, ProductionCounters, ProformaPrintButton } from './WfProforma.tsx';

export type WfAction =
  | 'move'
  | 'hold'
  | 'resume'
  | 'cancel'
  | 'reopen'
  | 'assign'
  | 'note'
  | 'approve'
  | 'request_approval'
  | 'review'
  | 'production'
  | 'reprint'
  | 'rework'
  | 'dispatch'
  | 'upload'
  | 'setup'
  | 'edit_due';

export interface ActionTarget {
  action: WfAction;
  c: WfCase;
  /** Board drop target: pre-selected stage for 'move'. */
  toStage?: string;
  /** Production kind preset. */
  productionKind?: string;
}

const TITLES: Record<WfAction, string> = {
  move: 'Move case',
  hold: 'Put on hold',
  resume: 'Resume from hold',
  cancel: 'Cancel case',
  reopen: 'Reopen case',
  assign: 'Assign',
  note: 'Add note',
  approve: 'Record approval decision',
  request_approval: 'Request approval / share simulation',
  review: 'Doctor review',
  production: 'Production record',
  reprint: 'Reprint required',
  rework: 'Rework required',
  dispatch: 'Dispatch',
  upload: 'Upload file',
  setup: 'New setup / simulation version',
  edit_due: 'Priority & due date',
};

/** 1.4 MB, 812 KB — the unit a person reads, not raw bytes. */
function fmtBytes(n: number): string {
  if (n >= 1024 ** 3) return `${(n / 1024 ** 3).toFixed(1)} GB`;
  if (n >= 1024 ** 2) return `${(n / 1024 ** 2).toFixed(1)} MB`;
  if (n >= 1024) return `${Math.round(n / 1024)} KB`;
  return `${n} B`;
}

/** "4 min", "40 s" — never "247 seconds". */
function fmtEta(s: number): string {
  if (s >= 3600) return `${Math.round(s / 3600)} h`;
  if (s >= 90) return `${Math.round(s / 60)} min`;
  return `${s} s`;
}

export function CaseActionModal({ target, onClose, onDone }: { target: ActionTarget | null; onClose: () => void; onDone: (c?: WfCase | null) => void }) {
  if (!target) return null;
  return (
    <Modal open onClose={onClose} title={`${TITLES[target.action]} - ${target.c.patientName} · ${target.c.caseNumber}`} wide={target.action === 'production' || target.action === 'move'}>
      <ActionBody key={`${target.action}:${target.c.id}`} target={target} onClose={onClose} onDone={onDone} />
    </Modal>
  );
}

function useSubmit(onDone: (c?: WfCase | null) => void) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const run = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    setError(null);
    try {
      const r = await fn();
      const c = r && typeof r === 'object' && !isDeferred(r) ? ((r as { case?: WfCase }).case ?? (r as WfCase)) : null;
      onDone(c && 'stageKey' in (c as object) ? (c as WfCase) : null);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };
  return { busy, error, run };
}

function StageSelect({ value, onChange, stages, exclude }: { value: string; onChange: (v: string) => void; stages: WfStage[]; exclude?: string }) {
  const groups = useMemo(() => {
    const m = new Map<string, WfStage[]>();
    for (const s of stages) {
      if (!s.isActive || s.key === exclude) continue;
      const list = m.get(s.group) ?? [];
      list.push(s);
      m.set(s.group, list);
    }
    return [...m.entries()];
  }, [stages, exclude]);
  return (
    <select className={inputCls} value={value} onChange={(e) => onChange(e.target.value)}>
      <option value="">- choose stage -</option>
      {groups.map(([g, list]) => (
        <optgroup key={g} label={g.toUpperCase()}>
          {list.map((s) => (
            <option key={s.key} value={s.key}>
              {s.name}
            </option>
          ))}
        </optgroup>
      ))}
    </select>
  );
}

function StaffSelect({ value, onChange, allowNone = true }: { value: string; onChange: (v: string) => void; allowNone?: boolean }) {
  const { staff } = useWfConfig();
  return (
    <select className={inputCls} value={value} onChange={(e) => onChange(e.target.value)}>
      {allowNone && <option value="">- unassigned -</option>}
      {staff.map((s) => (
        <option key={s.id} value={s.id}>
          {s.name} ({s.role.replace(/_/g, ' ')})
        </option>
      ))}
    </select>
  );
}

function OptionSelect({ kind, value, onChange, placeholder = '- choose -' }: { kind: Parameters<ReturnType<typeof useWfConfig>['optionsOf']>[0]; value: string; onChange: (v: string) => void; placeholder?: string }) {
  const { optionsOf } = useWfConfig();
  return (
    <select className={inputCls} value={value} onChange={(e) => onChange(e.target.value)}>
      <option value="">{placeholder}</option>
      {optionsOf(kind).map((o) => (
        <option key={o.key} value={o.key}>
          {o.label}
        </option>
      ))}
    </select>
  );
}

function Footer({ busy, onClose, label = 'Save', tone = 'primary' as 'primary' | 'danger' | 'accent' }: { busy: boolean; onClose: () => void; label?: string; tone?: 'primary' | 'danger' | 'accent' }) {
  return (
    <div className="mt-4 flex justify-end gap-2">
      <Btn tone="ghost" type="button" onClick={onClose}>
        Cancel
      </Btn>
      <Btn type="submit" tone={tone} disabled={busy}>
        {busy ? 'Saving…' : label}
      </Btn>
    </div>
  );
}

function ActionBody({ target, onClose, onDone }: { target: ActionTarget; onClose: () => void; onDone: (c?: WfCase | null) => void }) {
  const { c, action } = target;
  const cfg = useWfConfig();
  const { user } = useAuth();
  const { busy, error, run } = useSubmit(onDone);

  // Only for the setup modal: list what is already on the case so a video uploaded earlier can be
  // attached without re-uploading it.
  useEffect(() => {
    if (action !== 'setup') return;
    let alive = true;
    wfApi
      .get(c.id)
      .then((ws) => {
        if (alive) setCaseFiles(ws.files.filter((f) => SETUP_MEDIA.has(f.category)));
      })
      .catch(() => undefined);
    return () => {
      alive = false;
    };
  }, [action, c.id]);
  const stage = cfg.stageMap.get(c.stageKey);
  const [comment, setComment] = useState('');
  const [toStage, setToStage] = useState(target.toStage ?? '');
  const [reason, setReason] = useState('');
  const [notes, setNotes] = useState('');
  const [staffId, setStaffId] = useState(c.assignedTo ?? '');
  const [reviewerId, setReviewerId] = useState(c.reviewerId ?? '');
  const [expectedAt, setExpectedAt] = useState('');
  const [noteKind, setNoteKind] = useState<string>('internal');
  const [checklist, setChecklist] = useState<Record<string, boolean>>(c.checklistState ?? {});
  const [prodInstr, setProdInstr] = useState(c.productionInstructions ?? '');
  const [decision, setDecision] = useState<'approved' | 'rejected' | 'changes_requested' | 'approved_with_instructions'>('approved');
  const [approvalKind, setApprovalKind] = useState<WfApprovalKind>('doctor');
  const [method, setMethod] = useState('');
  const [returnTo, setReturnTo] = useState('');
  // The pipeline no longer has separate internal-review stages, so the stage can't preselect a
  // queue any more; the reviewer picks one. The two queues themselves are still real.
  const [queue, setQueue] = useState<'mubeen_awais' | 'dr_ali'>('dr_ali');
  const [reviewDecision, setReviewDecision] = useState('approved');
  const [instructions, setInstructions] = useState('');
  const [prodKind, setProdKind] = useState(target.productionKind ?? (stage?.key === R.printing ? 'print_order' : stage?.key === R.packaging ? 'packaging' : 'print_order'));
  const [prodStatus, setProdStatus] = useState<'open' | 'in_progress' | 'completed' | 'failed'>('completed');
  const [prodData, setProdData] = useState<Record<string, string>>({});
  const [prodChecklist, setProdChecklist] = useState<Array<{ label: string; done: boolean }>>([]);
  const [moveAfter, setMoveAfter] = useState('');
  const [courier, setCourier] = useState(c.courier ?? '');
  const [tracking, setTracking] = useState(c.trackingNumber ?? '');
  const [address, setAddress] = useState(c.deliveryAddress ?? '');
  const [receiver, setReceiver] = useState('');
  const [contents, setContents] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [uploadPct, setUploadPct] = useState<number | null>(null);
  const [uploadStats, setUploadStats] = useState<UploadStats | null>(null);
  const [uploadNote, setUploadNote] = useState<string | null>(null);
  const [category, setCategory] = useState('');
  const [description, setDescription] = useState('');
  const [setupKind, setSetupKind] = useState<string>('setup_3d');
  // The setup version's media: either a file already on the case, or one uploaded right here.
  const [setupFileId, setSetupFileId] = useState<string>('');
  const [caseFiles, setCaseFiles] = useState<WfFile[]>([]);
  // A setup version WITH media is shared with the doctor on save, and the doctor is approving the
  // cost as well as the shape — so the aligner count has to exist first. Collected here when the
  // case doesn't have one yet, rather than failing the save with a server error.
  const [setupUpper, setSetupUpper] = useState('');
  const [setupLower, setSetupLower] = useState('');
  const [setupDiscount, setSetupDiscount] = useState<AlignerDiscount | null>(null);
  const pricing = useAlignerPricing();
  const [priority, setPriority] = useState(c.priority);
  const [dueDate, setDueDate] = useState(c.dueDate ?? '');
  const [simReady, setSimReady] = useState(true);

  // Production checklist templates from config (packaging / touch-up option lists or stage checklist).
  useEffect(() => {
    if (action !== 'production') return;
    let items: string[] = [];
    if (prodKind === 'packaging') items = cfg.optionsOf('packaging_checklist').map((o) => o.label);
    if (!items.length && prodKind === 'packaging') items = cfg.stageMap.get('packaging')?.checklist ?? [];
    if (prodKind === 'touch_up') items = cfg.optionsOf('touchup_checklist').map((o) => o.label);
    if (!items.length && prodKind === 'touch_up') items = cfg.stageMap.get(R.packaging)?.checklist ?? [];
    setProdChecklist(items.map((label) => ({ label, done: false })));
    setProdData({});
  }, [action, prodKind, cfg]);

  const targetStage = cfg.stageMap.get(toStage);
  const leavingChecklist = stage?.checklist ?? [];
  const needsChecklist = action === 'move' && leavingChecklist.length > 0 && targetStage && targetStage.group !== 'special';
  const needsProdInstr = action === 'move' && targetStage?.requiredFields.includes('productionInstructions') && !c.productionInstructions;
  // Moving into Dispatched needs somewhere to send the box. Collect it here instead of bouncing the
  // user to the Dispatch action with a "record dispatch details first" error.
  const needsDispatch = action === 'move' && targetStage?.key === R.dispatch;
  const dispatchDetails = { courier: courier || null, trackingNumber: tracking || null, address: address || null, receiver: receiver || null, contents: contents || null };
  const dispatchOk = !!(courier || receiver || address);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    switch (action) {
      case 'move':
        return run(() => wfApi.move(c.id, { toStage, comment: comment || null, checklistState: needsChecklist ? checklist : undefined, patch: needsProdInstr && prodInstr ? { productionInstructions: prodInstr } : undefined, dispatch: needsDispatch ? dispatchDetails : undefined }));
      case 'hold':
        return run(() => wfApi.hold(c.id, { reason, responsibleUserId: staffId || null, expectedAt: expectedAt || null, notes: notes || null }));
      case 'resume':
        return run(() => wfApi.resume(c.id, { toStage: toStage || null, comment: comment || null }));
      case 'cancel':
        return run(() => wfApi.cancel(c.id, { reason, notes: notes || null }));
      case 'reopen':
        return run(() => wfApi.reopen(c.id, { toStage: toStage || null, comment: comment || null }));
      case 'assign':
        return run(() => wfApi.assign(c.id, { assignedTo: staffId || null, reviewerId: reviewerId || null }));
      case 'note':
        return run(() => wfApi.addNote(c.id, { kind: noteKind, body: notes }));
      case 'request_approval':
        return run(() => wfApi.requestApproval(c.id, { kind: approvalKind, method: method || null, comments: comment || null, simulationReady: simReady }));
      case 'approve':
        return run(() => wfApi.decideApproval(c.id, { kind: approvalKind, decision, method: method || null, comments: comment || null, reason: reason || null, returnToStage: returnTo || null, nextStage: toStage || null }));
      case 'review':
        return run(() => wfApi.review(c.id, { queue, decision: reviewDecision, comment: comment || null, instructions: instructions || null, nextStage: toStage || null, holdReason: reviewDecision === 'on_hold' ? reason || 'doctor_clarification' : null }));
      case 'production':
        return run(() => wfApi.production(c.id, { kind: prodKind, status: prodStatus, data: prodData, checklist: prodChecklist, notes: notes || null, moveTo: moveAfter || null }));
      case 'reprint':
      case 'rework':
        return run(() => wfApi.rework(c.id, { kind: action, reason, instructions: instructions || null, toStage: toStage || null }));
      case 'dispatch':
        return run(() => wfApi.dispatch(c.id, { courier: courier || null, trackingNumber: tracking || null, address: address || null, receiver: receiver || null, notes: notes || null, contents: contents || null }));
      case 'upload':
        return run(async () => {
          if (!file) throw new Error('Choose a file');
          setUploadPct(0);
          setUploadStats(null);
          setUploadNote(null);
          const out = await uploadCaseFile(c.id, file, { category: category || undefined, description: description || undefined }, (pct, stats) => {
            setUploadPct(pct);
            setUploadStats(stats ?? null);
          });
          if (!isArchiveOutcome(out) && out.autoShared) {
            setUploadNote('Video shared with the doctor for approval.');
          } else if (!isArchiveOutcome(out) && out.autoShareBlocked === 'trays_required') {
            setUploadNote('Uploaded. Set the number of aligners on this case and the video will be shared with the doctor.');
          }
          if (isArchiveOutcome(out)) {
            setUploadNote(out.unavailable ? `Kept ${out.archive} as a file (RAR extraction unavailable on server)` : `Extracted ${out.extracted.length} file${out.extracted.length === 1 ? '' : 's'} from ${out.archive}${out.skipped.length ? ` · skipped ${out.skipped.length}: ${out.skipped.map((x) => `${x.name} (${x.reason})`).join('; ')}` : ''}`);
          }
          setUploadPct(100);
          await new Promise((r) => setTimeout(r, 600));
          return null;
        });
      case 'setup':
        return run(async () => {
          // Upload first when a new file was chosen, so the version is created already pointing at
          // it — a version saved without its media is exactly the gap that made this flow useless.
          let fileId = setupFileId || null;
          const traysNeeded = !(c.trays && c.trays > 0) && (!!file || !!setupFileId);
          if (traysNeeded) {
            const u = Math.max(0, Number(setupUpper) || 0);
            const l = Math.max(0, Number(setupLower) || 0);
            if (u + l <= 0) throw new Error('Enter the upper / lower aligner numbers first');
            await wfApi.patch(c.id, { upperTrays: u, lowerTrays: l, trays: u + l, discountType: setupDiscount?.type ?? null, discountValue: setupDiscount?.value ?? null });
          }
          if (file) {
            const out = await uploadCaseFile(
              c.id,
              file,
              { category: setupKind === 'video' ? 'video' : 'setup', description: notes || undefined },
              setUploadPct,
            );
            fileId = isArchiveOutcome(out) ? (out.extracted[0]?.id ?? null) : out.id;
          }
          return wfApi.addSetupVersion(c.id, {
            kind: setupKind,
            notes: notes || null,
            fileId,
            status: fileId ? 'shared' : undefined,
          });
        });
      case 'edit_due':
        return run(() => wfApi.patch(c.id, { priority, dueDate: dueDate || null }));
    }
  };

  const commentField = (label = 'Comment') => (
    <Field label={label}>
      <textarea className={inputCls} rows={2} value={comment} onChange={(e) => setComment(e.target.value)} />
    </Field>
  );

  return (
    <form onSubmit={submit} className="space-y-3">
      <ErrorText>{error}</ErrorText>
      {action === 'move' && (
        <>
          <Field label="Move to stage">
            <StageSelect value={toStage} onChange={setToStage} stages={cfg.stages} exclude={c.stageKey} />
          </Field>
          {targetStage?.requiredFields.length ? <p className="text-xs text-muted">Required before entering: {targetStage.requiredFields.join(', ')}</p> : null}
          {PRODUCTION_COUNT_STAGES.includes(c.stageKey) && c.stageKey !== R.packaging && (
            <div className="rounded border border-line p-3">
              <ProductionCounters c={c} compact />
              <p className="mt-2 text-xs text-muted">Record how many upper / lower aligners this stage has finished before moving on. The next stage sees the same numbers.</p>
            </div>
          )}
          {targetStage?.key === R.packaging && (
            <div className="flex flex-wrap items-center gap-2 rounded border border-line p-3 text-xs text-muted">
              <span>The proforma (models, upper / lower aligner numbers) is packed with the aligners.</span>
              <ProformaPrintButton caseId={c.id} label="Print proforma for the box" />
            </div>
          )}
          {needsProdInstr && (
            <Field label="Production instructions (required)">
              <textarea className={inputCls} rows={2} value={prodInstr} onChange={(e) => setProdInstr(e.target.value)} placeholder="Models, upper/lower, layer info, special instructions" />
            </Field>
          )}
          {needsChecklist && (
            <div className="rounded border border-line p-3">
              <div className="mb-2 text-xs font-semibold uppercase text-muted">{stage?.name} checklist - must be complete to leave this stage</div>
              {leavingChecklist.map((item) => (
                <label key={item} className="flex items-center gap-2 py-0.5 text-sm">
                  <input type="checkbox" checked={!!checklist[item]} onChange={(e) => setChecklist({ ...checklist, [item]: e.target.checked })} /> {item}
                </label>
              ))}
              <button type="button" className="mt-1 text-xs text-accent underline" onClick={() => setChecklist(Object.fromEntries(leavingChecklist.map((i) => [i, true])))}>
                Mark all done
              </button>
            </div>
          )}
          {needsDispatch && (
            <div className="rounded border border-line p-3">
              <div className="mb-2 text-xs font-semibold uppercase text-muted">Dispatch details - courier, receiver or address is required</div>
              <div className="grid grid-cols-1 gap-x-4 gap-y-3 md:grid-cols-2">
                <Field label="Courier">
                  <OptionSelect kind="courier" value={courier} onChange={setCourier} />
                </Field>
                <Field label="Tracking number">
                  <input className={inputCls} value={tracking} onChange={(e) => setTracking(e.target.value)} />
                </Field>
              </div>
              <Field label="Receiver">
                <input className={inputCls} value={receiver} onChange={(e) => setReceiver(e.target.value)} placeholder="Clinic / person receiving" />
              </Field>
              <Field label="Delivery address">
                <textarea className={inputCls} rows={2} value={address} onChange={(e) => setAddress(e.target.value)} placeholder="Street, area, city" />
              </Field>
              <Field label="Package contents">
                <input className={inputCls} value={contents} onChange={(e) => setContents(e.target.value)} placeholder="e.g. Upper 1-12, Lower 1-10, retainer, box" />
              </Field>
            </div>
          )}
          {commentField()}
          <Footer busy={busy || !toStage || (needsDispatch && !dispatchOk)} onClose={onClose} label={needsDispatch ? 'Mark dispatched' : 'Move'} />
        </>
      )}
      {action === 'hold' && (
        <>
          <Field label="Hold reason (required)">
            <OptionSelect kind="hold_reason" value={reason} onChange={setReason} />
          </Field>
          <Field label="Person responsible">
            <StaffSelect value={staffId} onChange={setStaffId} />
          </Field>
          <Field label="Expected resolution date">
            <input type="date" className={inputCls} value={expectedAt} onChange={(e) => setExpectedAt(e.target.value)} />
          </Field>
          <Field label="Notes">
            <textarea className={inputCls} rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
          </Field>
          <Footer busy={busy || !reason} onClose={onClose} label="Put on hold" />
        </>
      )}
      {(action === 'resume' || action === 'reopen') && (
        <>
          <Field label={`Return to stage (default: ${cfg.stageName(c.previousStageKey) || 'New / Received'})`}>
            <StageSelect value={toStage} onChange={setToStage} stages={cfg.stages} exclude={c.stageKey} />
          </Field>
          {commentField()}
          <Footer busy={busy} onClose={onClose} label={action === 'resume' ? 'Resume' : 'Reopen'} />
        </>
      )}
      {action === 'cancel' && (
        <>
          <p className="text-sm text-ink">The case stays in history and can be reopened by a manager. Nothing is deleted.</p>
          <Field label="Cancellation reason (required)">
            <OptionSelect kind="cancel_reason" value={reason} onChange={setReason} />
          </Field>
          <Field label="Notes">
            <textarea className={inputCls} rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
          </Field>
          <Footer busy={busy || !reason} onClose={onClose} label="Cancel case" tone="danger" />
        </>
      )}
      {action === 'assign' && (
        <>
          <Field label="Assigned staff">
            <StaffSelect value={staffId} onChange={setStaffId} />
          </Field>
          <Field label="Reviewer / doctor">
            <StaffSelect value={reviewerId} onChange={setReviewerId} />
          </Field>
          <Footer busy={busy} onClose={onClose} label="Assign" />
        </>
      )}
      {action === 'note' && (
        <>
          <Field label="Note type">
            <select className={inputCls} value={noteKind} onChange={(e) => setNoteKind(e.target.value)}>
              {WF_NOTE_KINDS.map((k) => (
                <option key={k} value={k}>
                  {k.replace(/^\w/, (x) => x.toUpperCase())} notes
                </option>
              ))}
            </select>
          </Field>
          <Field label="Note">
            <textarea className={inputCls} rows={4} value={notes} onChange={(e) => setNotes(e.target.value)} autoFocus />
          </Field>
          <Footer busy={busy || !notes.trim()} onClose={onClose} label="Add note" />
        </>
      )}
      {action === 'request_approval' && (
        <>
          <Field label="Approval type">
            <select className={inputCls} value={approvalKind} onChange={(e) => setApprovalKind(e.target.value as WfApprovalKind)}>
              {WF_APPROVAL_KINDS.map((k) => (
                <option key={k} value={k}>
                  {WF_APPROVAL_KIND_LABELS[k]}
                </option>
              ))}
            </select>
          </Field>
          {approvalKind === 'doctor' && (
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={simReady} onChange={(e) => setSimReady(e.target.checked)} /> Setup / video is ready and shared with the doctor
            </label>
          )}
          <Field label="Method">
            <OptionSelect kind="approval_method" value={method} onChange={setMethod} />
          </Field>
          {commentField('Comments')}
          <Footer busy={busy} onClose={onClose} label="Record request" />
        </>
      )}
      {action === 'approve' && (
        <>
          <div className="grid grid-cols-1 gap-x-4 gap-y-3">
            <Field label="Approval type">
              <select className={inputCls} value={approvalKind} onChange={(e) => setApprovalKind(e.target.value as WfApprovalKind)}>
                {WF_APPROVAL_KINDS.map((k) => (
                  <option key={k} value={k}>
                    {WF_APPROVAL_KIND_LABELS[k]}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Decision">
              <select className={inputCls} value={decision} onChange={(e) => setDecision(e.target.value as typeof decision)}>
                <option value="approved">Approved</option>
                <option value="approved_with_instructions">Approved with instructions</option>
                <option value="changes_requested">Changes requested</option>
                <option value="rejected">Rejected</option>
              </select>
            </Field>
          </div>
          <Field label="Method">
            <OptionSelect kind="approval_method" value={method} onChange={setMethod} />
          </Field>
          {(decision === 'rejected' || decision === 'changes_requested') && (
            <>
              <Field label="Reason (required)">
                <textarea className={inputCls} rows={2} value={reason} onChange={(e) => setReason(e.target.value)} />
              </Field>
              <Field label={`Return case to (default: ${cfg.stageName(c.previousStageKey && c.previousStageKey !== R.onHold ? c.previousStageKey : R.design)})`}>
                <StageSelect value={returnTo} onChange={setReturnTo} stages={cfg.stages} exclude={c.stageKey} />
              </Field>
            </>
          )}
          {(decision === 'approved' || decision === 'approved_with_instructions') && (
            <Field label={`Then move to (default: ${approvalKind === 'production' ? 'Proceed for Printing' : 'Approved'})`}>
              <StageSelect value={toStage} onChange={setToStage} stages={cfg.stages} exclude={c.stageKey} />
            </Field>
          )}
          {commentField(decision === 'approved_with_instructions' ? 'Instructions (saved as patient note)' : 'Comments')}
          <Footer busy={busy || ((decision === 'rejected' || decision === 'changes_requested') && !reason.trim())} onClose={onClose} label="Record decision" tone={decision === 'rejected' ? 'danger' : 'accent'} />
        </>
      )}
      {action === 'review' && (
        <>
          <div className="grid grid-cols-1 gap-x-4 gap-y-3">
            <Field label="Review queue">
              <select className={inputCls} value={queue} onChange={(e) => setQueue(e.target.value as typeof queue)}>
                <option value="mubeen_awais">Mubeen / Awais</option>
                <option value="dr_ali">Dr Ali</option>
              </select>
            </Field>
            <Field label="Decision">
              <select className={inputCls} value={reviewDecision} onChange={(e) => setReviewDecision(e.target.value)}>
                <option value="approved">Approve</option>
                <option value="changes_requested">Request changes</option>
                <option value="rejected">Reject</option>
                <option value="on_hold">Put on hold</option>
                <option value="commented">Comment only</option>
              </select>
            </Field>
          </div>
          {reviewDecision === 'on_hold' && (
            <Field label="Hold reason">
              <OptionSelect kind="hold_reason" value={reason} onChange={setReason} />
            </Field>
          )}
          {reviewDecision !== 'on_hold' && reviewDecision !== 'commented' && (
            <Field label={`Next stage (default: ${reviewDecision === 'approved' ? cfg.stageName(R.doctorApproval) : cfg.stageName(c.previousStageKey && c.previousStageKey !== R.onHold ? c.previousStageKey : R.design)})`}>
              <StageSelect value={toStage} onChange={setToStage} stages={cfg.stages} exclude={c.stageKey} />
            </Field>
          )}
          <Field label="Instructions (saved as doctor note)">
            <textarea className={inputCls} rows={2} value={instructions} onChange={(e) => setInstructions(e.target.value)} />
          </Field>
          {commentField()}
          <p className="text-xs text-muted">Recorded as reviewed by {user?.displayName}.</p>
          <Footer busy={busy} onClose={onClose} label="Submit review" tone="accent" />
        </>
      )}
      {action === 'production' && (
        <>
          <div className="grid grid-cols-1 gap-x-4 gap-y-3">
            <Field label="Record type">
              <select className={inputCls} value={prodKind} onChange={(e) => setProdKind(e.target.value)}>
                <option value="print_order">Print order (Proceed for Printing)</option>
                <option value="print_run">Print run (Printing in Progress)</option>
                <option value="print_qc">Print QC (Print Complete)</option>
                <option value="touch_up">Final touch up</option>
                <option value="finishing">Finishing</option>
                <option value="qc">Quality check</option>
                <option value="packaging">Packaging</option>
              </select>
            </Field>
            <Field label="Status">
              <select className={inputCls} value={prodStatus} onChange={(e) => setProdStatus(e.target.value as typeof prodStatus)}>
                <option value="open">Open</option>
                <option value="in_progress">In progress</option>
                <option value="completed">Completed</option>
                <option value="failed">Failed</option>
              </select>
            </Field>
          </div>
          <ProductionFields kind={prodKind} data={prodData} onChange={setProdData} />
          {prodChecklist.length > 0 && (
            <div className="rounded border border-line p-3">
              <div className="mb-1 text-xs font-semibold uppercase text-muted">Checklist</div>
              {prodChecklist.map((item, i) => (
                <label key={item.label} className="flex items-center gap-2 py-0.5 text-sm">
                  <input type="checkbox" checked={item.done} onChange={(e) => setProdChecklist(prodChecklist.map((x, j) => (j === i ? { ...x, done: e.target.checked } : x)))} /> {item.label}
                </label>
              ))}
              <button type="button" className="mt-1 text-xs text-accent underline" onClick={() => setProdChecklist(prodChecklist.map((x) => ({ ...x, done: true })))}>
                Mark all done
              </button>
            </div>
          )}
          <Field label="Notes">
            <textarea className={inputCls} rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
          </Field>
          <Field label="Then move case to (optional)">
            <StageSelect value={moveAfter} onChange={setMoveAfter} stages={cfg.stages} exclude={c.stageKey} />
          </Field>
          <Footer busy={busy} onClose={onClose} label="Save record" />
        </>
      )}
      {(action === 'reprint' || action === 'rework') && (
        <>
          <Field label="Reason (required)">
            <textarea className={inputCls} rows={2} value={reason} onChange={(e) => setReason(e.target.value)} autoFocus />
          </Field>
          <Field label="Instructions">
            <textarea className={inputCls} rows={2} value={instructions} onChange={(e) => setInstructions(e.target.value)} />
          </Field>
          <Field label={`Return case to (default: ${action === 'reprint' ? 'Printing in Progress' : 'In Finishing'})`}>
            <StageSelect value={toStage} onChange={setToStage} stages={cfg.stages} exclude={c.stageKey} />
          </Field>
          <Footer busy={busy || !reason.trim()} onClose={onClose} label={action === 'reprint' ? 'Flag reprint' : 'Flag rework'} tone="danger" />
        </>
      )}
      {action === 'dispatch' && (
        <>
          <div className="grid grid-cols-1 gap-x-4 gap-y-3">
            <Field label="Courier">
              <OptionSelect kind="courier" value={courier} onChange={setCourier} />
            </Field>
            <Field label="Tracking number">
              <input className={inputCls} value={tracking} onChange={(e) => setTracking(e.target.value)} />
            </Field>
          </div>
          <Field label="Receiver">
            <input className={inputCls} value={receiver} onChange={(e) => setReceiver(e.target.value)} placeholder="Clinic / person receiving" />
          </Field>
          <Field label="Delivery address">
            <textarea className={inputCls} rows={2} value={address} onChange={(e) => setAddress(e.target.value)} />
          </Field>
          <Field label="Package contents">
            <input className={inputCls} value={contents} onChange={(e) => setContents(e.target.value)} placeholder="e.g. Upper 1-12, Lower 1-10, retainer, box" />
          </Field>
          <Field label="Dispatch notes">
            <textarea className={inputCls} rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
          </Field>
          <Footer busy={busy || (!courier && !receiver && !address)} onClose={onClose} label={c.stageKey === 'dispatched' ? 'Update tracking' : 'Mark dispatched'} tone="accent" />
        </>
      )}
      {action === 'upload' && (
        <>
          <Field label="File" hint="STL / scans / photos / PDFs, or a .zip / .rar - archives are extracted into individual files automatically.">
            <input type="file" accept=".stl,.ply,.obj,.3mf,.dcm,.jpg,.jpeg,.png,.gif,.webp,.heic,.bmp,.tif,.tiff,.mp4,.mov,.webm,.pdf,.doc,.docx,.txt,.xml,.csv,.xlsx,.zip,.rar" className="text-sm" onChange={(e) => { setFile(e.target.files?.[0] ?? null); setUploadPct(null); setUploadNote(null); }} />
            {file && /\.(zip|rar)$/i.test(file.name) && <p className="mt-1 text-xs font-medium text-muted">🗜 {file.name.split('.').pop()?.toUpperCase()} archive - contents will be validated and extracted.</p>}
            {uploadPct !== null && (
              <div className="mt-2">
                <div className="flex items-center justify-between text-xs text-muted">
                  <span>
                    {uploadPct < 100 ? 'Uploading…' : 'Upload complete'}
                    {uploadStats && uploadPct < 100 && (
                      <span className="tabnum ml-2 text-muted">
                        {fmtBytes(uploadStats.loaded)} of {fmtBytes(uploadStats.total)}
                        {uploadStats.bytesPerSecond > 0 && ` · ${fmtBytes(uploadStats.bytesPerSecond)}/s`}
                        {uploadStats.secondsLeft !== null && uploadStats.secondsLeft > 2 && ` · ${fmtEta(uploadStats.secondsLeft)} left`}
                      </span>
                    )}
                  </span>
                  <span className="font-mono">{uploadPct < 100 ? `${uploadPct}%` : <span className="font-bold text-status-progress-fg">✓</span>}</span>
                </div>
                <div className="mt-1 h-1.5 overflow-hidden rounded bg-surface-2"><div className={`h-full transition-all ${uploadPct < 100 ? 'bg-accent' : 'bg-status-progress-fg'}`} style={{ width: `${uploadPct}%` }} /></div>
                {uploadNote && <p className="mt-1 text-xs text-muted">{uploadNote}</p>}
              </div>
            )}
          </Field>
          <Field label="Category">
            <select className={inputCls} value={category} onChange={(e) => setCategory(e.target.value)}>
              <option value="">Auto-detect</option>
              {WF_FILE_CATEGORIES.map((k) => (
                <option key={k} value={k}>
                  {k.replace(/_/g, ' ')}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Description">
            <input className={inputCls} value={description} onChange={(e) => setDescription(e.target.value)} />
          </Field>
          <Footer busy={busy || !file} onClose={onClose} label="Upload" />
        </>
      )}
      {action === 'setup' && (
        <>
          <Field label="Video / file">
            <div className="space-y-2">
              {caseFiles.length > 0 && (
                <select
                  className={inputCls}
                  value={setupFileId}
                  onChange={(e) => {
                    setSetupFileId(e.target.value);
                    if (e.target.value) setFile(null);
                  }}
                  disabled={!!file}
                >
                  <option value="">- choose a file already on this case -</option>
                  {caseFiles.map((f) => (
                    <option key={f.id} value={f.id}>
                      {f.originalName} ({f.category.replace(/_/g, ' ')})
                    </option>
                  ))}
                </select>
              )}
              <input
                type="file"
                className="w-full text-sm"
                accept=".mp4,.mov,.webm,.m4v,.jpg,.jpeg,.png,.webp,.pdf"
                onChange={(e) => {
                  setFile(e.target.files?.[0] ?? null);
                  setSetupFileId('');
                }}
              />
              {uploadPct !== null && busy && (
                <div className="h-1.5 overflow-hidden rounded bg-surface-2">
                  <div className="h-full bg-accent transition-all" style={{ width: `${uploadPct}%` }} />
                </div>
              )}
              <p className="text-xs text-muted">
                {file || setupFileId
                  ? 'This is what the doctor will see, playing in their portal.'
                  : 'Optional - but without a file the doctor has nothing to review.'}
              </p>
            </div>
          </Field>
          {!(c.trays && c.trays > 0) && (
            <Field label="Number of aligners" hint={`PKR ${pricing.rateAtOrBelow.toLocaleString()} a tray up to ${pricing.threshold}, PKR ${pricing.rateAbove.toLocaleString()} above. Required before the doctor sees the setup.`}>
              <ArchTrayInputs upper={setupUpper} lower={setupLower} onChange={(u, l) => { setSetupUpper(u); setSetupLower(l); }} />
              <div className="mt-2 flex flex-wrap items-center gap-2">
                <span className="text-xs text-muted">Discount</span>
                <DiscountInput value={setupDiscount} onChange={setSetupDiscount} />
              </div>
              {(Number(setupUpper) || 0) + (Number(setupLower) || 0) > 0 && (
                <p className="mt-1 text-xs text-muted">
                  <QuoteLine quote={alignerQuote((Number(setupUpper) || 0) + (Number(setupLower) || 0), pricing, setupDiscount)} suffix="to the clinic" />
                </p>
              )}
            </Field>
          )}
          <Field label="Kind">
            <select className={inputCls} value={setupKind} onChange={(e) => setSetupKind(e.target.value)}>
              {WF_SETUP_KINDS.map((k) => (
                <option key={k} value={k}>
                  {k.replace(/_/g, ' ')}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Notes">
            <textarea className={inputCls} rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} />
          </Field>
          <p className="text-xs text-muted">
            Creates V{c.setupVersion + 1}. Earlier versions are kept and marked superseded.
            {(file || setupFileId) && ' The attached file is shared with the doctor for approval.'}
          </p>
          <Footer busy={busy} onClose={onClose} label="Create version" />
        </>
      )}
      {action === 'edit_due' && (
        <>
          <TurnaroundPicker
            value={priority}
            receivedAt={c.receivedAt}
            onChange={(p) => {
              setPriority(p);
              // Follow the new promise unless this case has a date somebody set deliberately.
              if (isDerivedDueDate(dueDate || null, c.receivedAt, priority)) {
                setDueDate(turnaroundDueDate(c.receivedAt, p));
              }
            }}
          />
          <Field label="Due / delivery date">
            <input type="date" className={inputCls} value={dueDate} onChange={(e) => setDueDate(e.target.value)} />
          </Field>
          <Footer busy={busy} onClose={onClose} />
        </>
      )}
    </form>
  );
}

function ProductionFields({ kind, data, onChange }: { kind: string; data: Record<string, string>; onChange: (d: Record<string, string>) => void }) {
  const set = (k: string, v: string) => onChange({ ...data, [k]: v });
  const F = (k: string, label: string, type = 'text') => (
    <Field label={label} key={k}>
      <input type={type} className={inputCls} value={data[k] ?? ''} onChange={(e) => set(k, e.target.value)} />
    </Field>
  );
  if (kind === 'print_order')
    return (
      <div className="grid grid-cols-1 gap-x-4 gap-y-3">
        {F('models', 'Number of models', 'number')}
        {F('arches', 'Upper / lower')}
        {F('layer', 'Layer information')}
        {F('special', 'Special instructions')}
      </div>
    );
  if (kind === 'print_run')
    return (
      <div className="grid grid-cols-1 gap-x-4 gap-y-3">
        {F('printer', 'Printer / assignment')}
        {F('expected', 'Expected completion', 'datetime-local')}
      </div>
    );
  if (kind === 'print_qc')
    return (
      <div className="grid grid-cols-1 gap-x-4 gap-y-3">
        {F('qc', 'Quality check result')}
        <Field label="Failed / reprint?">
          <select className={inputCls} value={data.failed ?? 'no'} onChange={(e) => set('failed', e.target.value)}>
            <option value="no">Passed</option>
            <option value="yes">Failed - needs reprint</option>
          </select>
        </Field>
      </div>
    );
  if (kind === 'finishing' || kind === 'qc')
    return (
      <div className="grid grid-cols-1 gap-x-4 gap-y-3">
        {F('qc', 'Quality check')}
        {F('rework', 'Rework reason (if any)')}
      </div>
    );
  return null;
}
